import { useState } from "react";
import axios from "axios";
import { Mail, MapPin, Phone, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { industries } from "@/data/industries";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    industry: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      setError("Please fill your name, email and a short message (5+ characters).");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        industry: form.industry || null,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Thanks — we'll be in touch within 24 hours.");
      setForm({ name: "", email: "", company: "", industry: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please email contact@pransam.com.";
      setError(typeof detail === "string" ? detail : "Submission failed.");
      toast.error("Could not submit — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="page-contact" className="pt-24">
      <section className="container-x pt-16 pb-8">
        <div className="font-mono-label text-xs text-[#00F0FF] mb-4">
          / Contact
        </div>
        <h1 className="font-display text-5xl sm:text-6xl text-white tracking-tight max-w-3xl leading-[1.02]">
          Tell us about your{" "}
          <span className="text-[#00F0FF]">hardest problem.</span>
        </h1>
        <p className="mt-6 text-white/60 max-w-2xl leading-relaxed">
          A senior engineer or partner will respond within 24 hours. No sales
          reps. No decks. Just a real conversation.
        </p>
      </section>

      <section className="container-x pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Info */}
          <aside className="lg:col-span-4">
            <div className="rounded-2xl border border-white/10 bg-[#0F0F13] p-8">
              <div className="font-mono-label text-[11px] text-[#00F0FF]">
                Direct
              </div>
              <div className="mt-6 space-y-5">
                <div className="flex items-start gap-3">
                  <Mail
                    size={16}
                    className="text-[#00F0FF] mt-1 shrink-0"
                    strokeWidth={1.75}
                  />
                  <div>
                    <div className="text-xs text-white/50 font-mono-label">
                      EMAIL
                    </div>
                    <a
                      href="mailto:contact@pransam.com"
                      data-testid="contact-info-email"
                      className="text-white hover:text-[#00F0FF] transition-colors"
                    >
                      contact@pransam.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone
                    size={16}
                    className="text-[#00F0FF] mt-1 shrink-0"
                    strokeWidth={1.75}
                  />
                  <div>
                    <div className="text-xs text-white/50 font-mono-label">
                      PARTNERSHIPS
                    </div>
                    <div className="text-white">partnerships@pransam.com</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin
                    size={16}
                    className="text-[#00F0FF] mt-1 shrink-0"
                    strokeWidth={1.75}
                  />
                  <div>
                    <div className="text-xs text-white/50 font-mono-label">
                      HEADQUARTERS
                    </div>
                    <div className="text-white">Bengaluru, India</div>
                  </div>
                </div>
              </div>

              <div className="divider-line my-8" />

              <div className="font-mono-label text-[11px] text-white/50 mb-3">
                RESPONSE SLA
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                Within 24 hours from Mon–Fri, 09:00–19:00 IST. Weekend
                inquiries answered on the next business day.
              </p>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-2xl border border-white/10 bg-[#0F0F13] p-8 md:p-10"
            >
              {done ? (
                <div
                  data-testid="contact-success"
                  className="min-h-[400px] flex flex-col items-start justify-center"
                >
                  <div className="w-14 h-14 rounded-full grid place-items-center bg-[#00FF66]/15 border border-[#00FF66]/40 text-[#00FF66]">
                    <CheckCircle2 size={26} />
                  </div>
                  <h2 className="mt-6 font-display text-3xl md:text-4xl text-white tracking-tight">
                    Message received.
                  </h2>
                  <p className="mt-3 text-white/60 max-w-lg leading-relaxed">
                    A senior member of the Pransam team will reach out within
                    24 hours. In the meantime, feel free to explore our
                    industry practices.
                  </p>
                  <button
                    type="button"
                    data-testid="contact-send-another"
                    onClick={() => setDone(false)}
                    className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 text-white/90 px-5 py-2.5 text-sm hover:bg-white/10 transition-colors"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="font-mono-label text-[10px] text-white/50 block mb-2">
                        NAME *
                      </label>
                      <Input
                        data-testid="contact-input-name"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Your full name"
                        className="bg-[#050505] border-white/10 text-white h-11"
                        required
                      />
                    </div>
                    <div>
                      <label className="font-mono-label text-[10px] text-white/50 block mb-2">
                        EMAIL *
                      </label>
                      <Input
                        data-testid="contact-input-email"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@company.com"
                        className="bg-[#050505] border-white/10 text-white h-11"
                        required
                      />
                    </div>
                    <div>
                      <label className="font-mono-label text-[10px] text-white/50 block mb-2">
                        COMPANY
                      </label>
                      <Input
                        data-testid="contact-input-company"
                        value={form.company}
                        onChange={update("company")}
                        placeholder="Where you work"
                        className="bg-[#050505] border-white/10 text-white h-11"
                      />
                    </div>
                    <div>
                      <label className="font-mono-label text-[10px] text-white/50 block mb-2">
                        INDUSTRY
                      </label>
                      <Select
                        value={form.industry}
                        onValueChange={update("industry")}
                      >
                        <SelectTrigger
                          data-testid="contact-select-industry"
                          className="bg-[#050505] border-white/10 text-white h-11"
                        >
                          <SelectValue placeholder="Select an industry" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
                          {industries.map((i) => (
                            <SelectItem
                              key={i.slug}
                              value={i.name}
                              data-testid={`contact-industry-option-${i.slug}`}
                            >
                              {i.name}
                            </SelectItem>
                          ))}
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mt-5">
                    <label className="font-mono-label text-[10px] text-white/50 block mb-2">
                      WHAT ARE YOU TRYING TO BUILD? *
                    </label>
                    <Textarea
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={update("message")}
                      rows={6}
                      placeholder="A few sentences on the problem, current stack and any timelines."
                      className="bg-[#050505] border-white/10 text-white resize-none"
                      required
                    />
                  </div>

                  {error && (
                    <div
                      data-testid="contact-error"
                      className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-4 py-3"
                    >
                      {error}
                    </div>
                  )}

                  <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-xs text-white/40 max-w-sm">
                      By submitting, you agree we can email you a response. We
                      do not share your data.
                    </p>
                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={loading}
                      className="inline-flex items-center gap-2 rounded-full bg-[#00F0FF] text-black font-semibold px-6 py-3 transition-transform duration-200 hover:-translate-y-0.5 hover-glow disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Sending
                        </>
                      ) : (
                        <>Send message</>
                      )}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}
